import _ from 'lodash';
import {SortDirection} from '@treez-ui/core/sharedTypings';
import React, {FunctionComponent, useContext} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import ProductTable, {ProductTableColumn} from '@treez-ui/core/table/ProductTable';
import {ProductField, ProductInCatalog} from '@treez/commons/sharedTypings/product';
import {useDispatch, useSelector} from 'react-redux';
import {ACCEPTED} from 'http-status-codes';
import {ModalType} from '@treez-ui/core/feedback/modals';
import {ButtonClass} from '@treez-ui/core/buttons';
import {
    generateSelectColumn,
    generateLinkStatusColumn,
    generateNameColumn,
    generateBrandColumn,
    amountColumn,
    typeColumn,
    subtypeColumn,
    skuColumn,
    priceColumn,
    lastUpdatedColumn,
} from '~/modules/widgets/products/ProductTableColumns';
import {BrandTreezPageParameters} from '../brandTreez/BrandTreezPage';
import {generateBrandTreezRoute} from '~/Routes';
import {determineIfAllProductsSelected, ProductListView} from '~/lib/product';
import {
    CurrentSelectionStateContext,
    DispatchCurrentSelectionAction,
    TypeOfSelectAction,
} from '~/lib/selectedProductContext';
import {
    openFeedbackModal,
    closeFeedbackModal,
} from '~/redux/boundActionCreators/UserFeedbackActions';
import {
    informUserOfErrorUnlinking,
    informUserOfErrorUnlinkingSuccess,
} from '~/redux/actionCreators/UserFeedbackCreators';
import {unlinkBrandProduct} from '~/utils/ProductAPI';
import {generateQueryString, getSearchQueryParameters, parseQueryString} from '~/lib/routing';
import {selectOrganizationId} from '~/redux/selectors/organizationSelectors';
import {CentralCatalogPageParameters} from './OrganizationCatalog';
import {
    setFetching,
    useOrgCatalogDispatcher,
    useOrgCatalogState,
} from '~/contexts/OrgCatalog/orgCatalogContext';
import {
    useProductFormContext,
    useProductFormStateDispatch,
} from '~/contexts/ProductForm/productFormContext';
import {openProductFormOnly} from '~/contexts/ProductForm/productFormActions';

const {ADD, CLEAR, REMOVE} = TypeOfSelectAction;

interface Props {
    highlightedProducts: ProductInCatalog[];
    combinedProducts: ProductInCatalog[];
}

export const OrganizationProductTable: FunctionComponent<Props> = (props: Props) => {
    const {highlightedProducts, combinedProducts} = props;

    const history = useHistory();
    const location = useLocation();

    let productChecked = false;
    let linkProductChecked = false;
    const {isProductFormOpen} = useProductFormContext();
    const productFormDispatch = useProductFormStateDispatch();

    const {fetching} = useOrgCatalogState();
    const orgCatalogDispatcher = useOrgCatalogDispatcher();

    const queryParams = parseQueryString(location.search) as CentralCatalogPageParameters;
    const searchParams = getSearchQueryParameters(queryParams);

    const {sortOrder, sortCategory} = searchParams;

    const hideStores = queryParams.hideStores === 'true';

    const selectedProducts = useContext(CurrentSelectionStateContext);
    const organizationId = useSelector(selectOrganizationId);

    const dispatchSelectAction = DispatchCurrentSelectionAction();
    const dispatch = useDispatch();

    const handleSelect = (isSelected: boolean, productSelected: ProductInCatalog) => {
        // setting this variable to true here, and verifying its value in row click event handler
        // to make sure Edit Product modal doesn't show up
        productChecked = !productChecked;

        dispatchSelectAction({
            type: isSelected ? ADD : REMOVE,
            products: [productSelected],
        });
    };

    const selectAll = (isSelected: boolean) => {
        if (isSelected) {
            dispatchSelectAction({
                type: ADD,
                products: combinedProducts,
            });
        } else {
            dispatchSelectAction({type: CLEAR});
        }
    };

    const selectedProductIds = _.map(selectedProducts, 'id');
    const highlightedProductIds = _.map(highlightedProducts, 'id');

    const allSelected = determineIfAllProductsSelected(combinedProducts, selectedProductIds);

    const goToLinkProduct = async (productToLink: ProductInCatalog) => {
        // setting this variable to true here, and verifying its value in row click event handler
        // to make sure Edit Product modal doesn't show up
        linkProductChecked = true;

        const {id: brandProductId, type, brandName} = productToLink;

        const linkParams = {
            view: ProductListView.TABLE,
            type: [type],
            brand: [brandName],
            pageSize: '25',
            page: '1',
            productToLink: String(brandProductId),
        } as Partial<BrandTreezPageParameters>;

        const brandLink = generateBrandTreezRoute(organizationId, linkParams);

        history.push({
            pathname: brandLink.pathname,
            search: brandLink.search,
            state: {
                productToLink,
                refferer: generateQueryString(location.search),
            },
        });
    };

    const unlinkProduct = async (product: ProductInCatalog) => {
        try {
            if (product) {
                const response = await unlinkBrandProduct(product.id);

                if (response.status === ACCEPTED) {
                    closeFeedbackModal();
                    dispatch(informUserOfErrorUnlinkingSuccess());
                    // Refresh the table to show that this product is now unlinked
                    orgCatalogDispatcher(setFetching(true));
                }
            } else {
                dispatch(informUserOfErrorUnlinking());
            }
        } catch (error) {
            console.error(error);
            dispatch(informUserOfErrorUnlinking());
        }
        // enables product row click event
        linkProductChecked = false;
    };

    const closeUnlinkModal = () => {
        closeFeedbackModal();
        // enables product row click event
        linkProductChecked = false;
    };

    const openUnlinkModal = async (product: ProductInCatalog) => {
        // setting this variable to true, and verifying its value in row click event handler
        // to make sure Edit Product modal doesn't show up
        linkProductChecked = true;

        if (product) {
            openFeedbackModal(() => unlinkProduct(product), 'Unlink BrandTreez Product', {
                bodyText: 'Are you sure you want to unlink BrandTreez product?',
                buttonClass: ButtonClass.DESTRUCTIVE,
                buttonText: 'Unlink',
                cancelOption: true,
                onCancel: closeUnlinkModal,
                type: ModalType.WARNING,
            });
        }
    };

    const determineClass = ({id}: ProductInCatalog) => {
        let determinedClass = '';

        if (_.includes(selectedProductIds, id) || _.includes(highlightedProductIds, id)) {
            determinedClass += 'selected-product';
        }

        return determinedClass;
    };

    const sort = (sortCategory: string, sortOrder: string) => {
        history.push({
            search: generateQueryString({
                ...queryParams,
                sortCategory,
                sortOrder,
            }),
        });
    };

    const onProductRowClick = (product: ProductInCatalog) => {
        // Adding a slight delay so that "product checkbox/link product" events are fired (when those controls are clicked) before row click event.
        // As a result we have the ability to check for those events and negate row click event, when checkbox or link product controls are clicked.
        setTimeout(() => {
            if (!isProductFormOpen && !productChecked && !linkProductChecked) {
                productFormDispatch(openProductFormOnly(product));
            }
        }, 50);
    };

    const productTableColumns = [
        generateSelectColumn(selectAll, allSelected, handleSelect),
        generateLinkStatusColumn(openUnlinkModal, goToLinkProduct),
        generateNameColumn(),
        generateBrandColumn(),
        amountColumn,
        typeColumn,
        subtypeColumn,
        skuColumn,
        priceColumn,
        lastUpdatedColumn,
    ] as ProductTableColumn[];

    return (
        <div className="organization-products-table-interior-container">
            <ProductTable
                columns={productTableColumns}
                determineClass={determineClass}
                disabledSortFields={[ProductField.PRICE]}
                values={combinedProducts}
                selectedValueIds={_.map(selectedProducts, 'id')}
                fetching={fetching}
                sortOrder={sortOrder as SortDirection}
                sortCategory={sortCategory}
                headerHeight={hideStores ? undefined : '80px'}
                sort={sort}
                rowOnClick={onProductRowClick}
            />
        </div>
    );
};
